import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';
import SEO from '../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../seo/pageSeo';

const guidePoints = [
  {
    num: '01',
    id: 'fabric-range',
    name: 'purchaseguide_3',
    desc: 'purchaseguide_4',
  },
  {
    num: '02',
    id: 'spreading-quality',
    name: 'purchaseguide_5',
    desc: 'purchaseguide_6',
  },
  {
    num: '03',
    id: 'spreading-speed',
    name: 'purchaseguide_7',
    desc: 'purchaseguide_8',
  },
  {
    num: '04',
    id: 'ease-of-use',
    name: 'purchaseguide_9',
    desc: 'purchaseguide_10',
  },
  {
    num: '05',
    id: 'machine-stability',
    name: 'purchaseguide_11',
    desc: 'purchaseguide_12',
  },
  {
    num: '06',
    id: 'system-intelligence',
    name: 'purchaseguide_13',
    desc: 'purchaseguide_14',
  },
  {
    num: '07',
    id: 'energy-efficiency',
    name: 'purchaseguide_15',
    desc: 'purchaseguide_16',
  },
];

const advantages = [
  {
    num: '01',
    id: 'fabric-adaptability',
    name: 'purchaseguide_18',
    desc: 'purchaseguide_19',
  },
  {
    num: '02',
    id: 'industry-leading',
    name: 'purchaseguide_20',
    desc: 'purchaseguide_21',
  },
  {
    num: '03',
    id: 'high-speed',
    name: 'purchaseguide_22',
    desc: 'purchaseguide_23',
  },
  {
    num: '04',
    id: 'intelligent-system',
    name: 'purchaseguide_24',
    desc: 'purchaseguide_25',
  },
  {
    num: '05',
    id: 'hardware-quality',
    name: 'purchaseguide_26',
    desc: 'purchaseguide_27',
  },
];

function GuideCard({ item, index }) {
  const cardRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      id={item.id}
      className="guide-card"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="guide-card__num">{item.num}</div>
      <div className="guide-card__body">
        <h3 className="guide-card__title">{t(item.name)}</h3>
        <p className="guide-card__desc">{t(item.desc).trim()}</p>
      </div>
    </div>
  );
}

function AdvCard({ item, index }) {
  const cardRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      id={item.id}
      className="adv-card"
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="adv-card__head">
        <div className="adv-card__num">{item.num}</div>
        <div className="adv-card__mark">
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2,6 5,9 10,3" />
          </svg>
        </div>
      </div>
      <div className="adv-card__divider" />
      <h3 className="adv-card__title">{t(item.name)}</h3>
      <p className="adv-card__desc">{t(item.desc).trim()}</p>
    </div>
  );
}

function SectionHeader({ eyebrow, title, align = 'left' }) {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`section-header section-header--${align}`}>
      <div className="section-header__eyebrow">{eyebrow}</div>
      <h2 className="section-header__title">{title}</h2>
      <div className="section-header__rule" />
    </div>
  );
}

const navSections = [
  { labelKey: 'purchaseguide_nav_key_points', id: 'key-points' },
  { labelKey: 'purchaseguide_nav_fabric_range', id: 'fabric-range' },
  { labelKey: 'purchaseguide_nav_spreading_quality', id: 'spreading-quality' },
  { labelKey: 'purchaseguide_nav_spreading_speed', id: 'spreading-speed' },
  { labelKey: 'purchaseguide_nav_ease_of_use', id: 'ease-of-use' },
  { labelKey: 'purchaseguide_nav_machine_stability', id: 'machine-stability' },
  { labelKey: 'purchaseguide_nav_system_intelligence', id: 'system-intelligence' },
  { labelKey: 'purchaseguide_nav_energy_efficiency', id: 'energy-efficiency' },
  { labelKey: 'purchaseguide_nav_advantages', id: 'advantages' },
  { labelKey: 'purchaseguide_nav_fabric_adaptability', id: 'fabric-adaptability' },
  { labelKey: 'purchaseguide_nav_industry_leading', id: 'industry-leading' },
  { labelKey: 'purchaseguide_nav_high_speed', id: 'high-speed' },
  { labelKey: 'purchaseguide_nav_intelligent_system', id: 'intelligent-system' },
  { labelKey: 'purchaseguide_nav_hardware_quality', id: 'hardware-quality' },
];

export default function ChooseSpreadingMachine() {
  const { t } = useTranslation();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
    <SEO
        pageKey="purchase_guide"
        title={PAGE_SEO.purchase_guide.title}
        description={PAGE_SEO.purchase_guide.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.purchase_guide.slug}
    />
    <div className="guide-page">
      {/* Hero */}
      <div className="guide-hero">
        <img
          className="guide-hero__img"
          src="https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/big1.webp"
          alt="guide"
        />
        <div className="guide-hero__overlay" />
        <div className="guide-hero__content">
          <div className="guide-hero__eyebrow">
            {t('purchaseguide_hero_eyebrow')}
          </div>
          <h1 className="guide-hero__title">
            {t('purchaseguide_hero_title')}
          </h1>
          <p className="guide-hero__sub">
            {t('purchaseguide_hero_sub')}
          </p>
          <div className="guide-hero__rule" />
        </div>
      </div>

      {/* Main content */}
      <div className="guide-body">
        {/* Sticky nav */}
        <div className="guide-nav">
          {navSections.map((s) => (
            <button
              key={s.id}
              className="guide-nav__item"
              onClick={() => scrollTo(s.id)}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>

        {/* Section 1: 选购要点 */}
        <div id="key-points" className="guide-section">
          <SectionHeader
            eyebrow={t('purchaseguide_section1_eyebrow')}
            title={t('purchaseguide_section1_title')}
          />

          <div className="guide-cards">
            {guidePoints.map((item, i) => (
              <GuideCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>

        {/* Section 2: 小麦优势 */}
        <div id="advantages" className="guide-section guide-section--alt">
          <SectionHeader
            eyebrow={t('purchaseguide_section2_eyebrow')}
            title={t('purchaseguide_section2_title')}
          />

          <div className="adv-cards">
            {advantages.map((item, i) => (
              <AdvCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
